import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import clsx from 'clsx';
import { MessageSquare } from 'lucide-react';
import { memo } from 'react';
import { ObligationStrength, RequirementCategory } from '../../../types/proposal';
import { AmendmentBadge } from '../v2/amendment/AmendmentBadge';
import { StatusDropdown } from './StatusDropdown';
const CATEGORY_LABELS = {
    [RequirementCategory.PROPOSAL_INSTRUCTION]: 'Proposal',
    [RequirementCategory.FORMAT_REQUIREMENT]: 'Format',
    [RequirementCategory.SUBMISSION_REQUIREMENT]: 'Submission',
    [RequirementCategory.SCOPE_REQUIREMENT]: 'Scope',
    [RequirementCategory.PERFORMANCE_STANDARD]: 'Performance',
    [RequirementCategory.DELIVERABLE]: 'Deliverable',
    [RequirementCategory.EVALUATION_CRITERION]: 'Evaluation',
    [RequirementCategory.QUALIFICATION]: 'Qualification',
    [RequirementCategory.COMPLIANCE_REQUIREMENT]: 'Compliance',
    [RequirementCategory.CLAUSE_FLOWDOWN]: 'Clause',
    [RequirementCategory.SECURITY_REQUIREMENT]: 'Security',
    [RequirementCategory.KEY_PERSONNEL]: 'Personnel',
    [RequirementCategory.REPORTING_REQUIREMENT]: 'Reporting',
    [RequirementCategory.PERIOD_OF_PERFORMANCE]: 'PoP',
    [RequirementCategory.PLACE_OF_PERFORMANCE]: 'Place',
    [RequirementCategory.CLIN]: 'CLIN',
    [RequirementCategory.TASK_REQUIREMENT]: 'Task',
    [RequirementCategory.OTHER]: 'Other'
};
const CATEGORY_COLORS = {
    [RequirementCategory.PROPOSAL_INSTRUCTION]: 'bg-violet-50 text-violet-700 ring-violet-200',
    [RequirementCategory.FORMAT_REQUIREMENT]: 'bg-cyan-50 text-cyan-700 ring-cyan-200',
    [RequirementCategory.SCOPE_REQUIREMENT]: 'bg-blue-50 text-blue-700 ring-blue-200',
    [RequirementCategory.PERFORMANCE_STANDARD]: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
    [RequirementCategory.DELIVERABLE]: 'bg-amber-50 text-amber-700 ring-amber-200',
    [RequirementCategory.EVALUATION_CRITERION]: 'bg-rose-50 text-rose-700 ring-rose-200',
    [RequirementCategory.SECURITY_REQUIREMENT]: 'bg-red-50 text-red-700 ring-red-200',
    [RequirementCategory.KEY_PERSONNEL]: 'bg-pink-50 text-pink-700 ring-pink-200',
    [RequirementCategory.COMPLIANCE_REQUIREMENT]: 'bg-slate-50 text-slate-700 ring-slate-200'
};
const RESPONSE_ACTION_LABELS = {
    WRITE_APPROACH: 'Write',
    PROVIDE_EVIDENCE: 'Evidence',
    COMPLY_FORMAT: 'Format',
    SUBMIT_ACTION: 'Submit',
    ACKNOWLEDGE: 'Ack',
    PRICE: 'Price',
    STAFF: 'Staff'
};
const RESPONSE_ACTION_COLORS = {
    WRITE_APPROACH: 'bg-purple-100 text-purple-700',
    PROVIDE_EVIDENCE: 'bg-amber-100 text-amber-700',
    COMPLY_FORMAT: 'bg-cyan-100 text-cyan-700',
    SUBMIT_ACTION: 'bg-indigo-100 text-indigo-700',
    ACKNOWLEDGE: 'bg-gray-100 text-gray-600',
    PRICE: 'bg-green-100 text-green-700',
    STAFF: 'bg-pink-100 text-pink-700'
};
const OBLIGATION_COLORS = {
    [ObligationStrength.SHALL]: 'text-red-600',
    [ObligationStrength.MUST]: 'text-red-600',
    [ObligationStrength.WILL]: 'text-orange-600',
    [ObligationStrength.SHOULD]: 'text-yellow-600',
    [ObligationStrength.MAY]: 'text-gray-500'
};
export const RequirementRow = /*#__PURE__*/ memo(function RequirementRow({ requirement, isSelected, onSelect, onStatusChange, isUpdating, onRowClick, onAskAI }) {
    const handleCheckboxChange = (e)=>{
        onSelect(e.target.checked);
    };
    const handleStatusChange = (status)=>{
        onStatusChange(requirement.requirementId, status);
    };
    const categoryLabel = CATEGORY_LABELS[requirement.category] || requirement.category;
    const categoryColor = CATEGORY_COLORS[requirement.category] || 'bg-gray-50 text-gray-700 ring-gray-200';
    return /*#__PURE__*/ _jsxs("tr", {
        "data-testid": `${Testids.compliance.requirementRow}-${requirement.requirementId}`,
        className: clsx('group/row border-b border-gray-100 hover:bg-gray-50/50 transition-colors', onRowClick && 'cursor-pointer'),
        onClick: ()=>onRowClick?.(requirement),
        children: [
            /*#__PURE__*/ _jsx("td", {
                className: "pl-4 pr-2 py-3 w-10",
                children: /*#__PURE__*/ _jsx("input", {
                    type: "checkbox",
                    checked: isSelected,
                    onChange: handleCheckboxChange,
                    onClick: (e)=>e.stopPropagation(),
                    className: "h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "px-3 py-3",
                children: /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "text-sm text-gray-900 text-left w-full leading-relaxed line-clamp-3",
                            children: requirement.source.exactText || requirement.requirementId
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-1 flex items-center gap-2 text-[11px] text-gray-400",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "truncate max-w-[200px]",
                                    children: requirement.source.filename
                                }),
                                requirement.source.sectionNumber && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "·"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "§",
                                                requirement.source.sectionNumber
                                            ]
                                        })
                                    ]
                                }),
                                requirement.source.pageNumber > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "·"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "p.",
                                                requirement.source.pageNumber
                                            ]
                                        })
                                    ]
                                }),
                                (requirement.amendmentInfo?.amendmentNumber || (requirement.amendmentSequence ?? 0) > 0) && /*#__PURE__*/ _jsx(AmendmentBadge, {
                                    amendmentNumber: requirement.amendmentInfo?.amendmentNumber,
                                    amendmentSequence: requirement.amendmentSequence,
                                    isCurrent: requirement.isCurrentVersion,
                                    sourceFilename: requirement.sourceFilename
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "px-3 py-3 w-28",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col gap-1",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: clsx('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ring-1 ring-inset', categoryColor),
                            children: categoryLabel
                        }),
                        requirement.responseAction && /*#__PURE__*/ _jsx("span", {
                            className: clsx('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium', RESPONSE_ACTION_COLORS[requirement.responseAction] || 'bg-gray-100 text-gray-700'),
                            children: RESPONSE_ACTION_LABELS[requirement.responseAction] || requirement.responseAction
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "px-3 py-3 w-16 text-center",
                children: /*#__PURE__*/ _jsx("span", {
                    className: clsx('text-[11px] font-bold', OBLIGATION_COLORS[requirement.obligationStrength] || 'text-gray-500'),
                    children: requirement.obligationStrength
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "px-3 py-3 w-44",
                onClick: (e)=>e.stopPropagation(),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-1.5",
                    children: [
                        /*#__PURE__*/ _jsx(StatusDropdown, {
                            value: requirement.status,
                            onChange: handleStatusChange,
                            disabled: isUpdating,
                            testid: `${Testids.compliance.statusSelect}-${requirement.requirementId}`
                        }),
                        onAskAI && /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: ()=>onAskAI(requirement),
                            className: "opacity-0 group-hover/row:opacity-100 p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all",
                            title: "Ask AI about this requirement",
                            children: /*#__PURE__*/ _jsx(MessageSquare, {
                                className: "w-3.5 h-3.5"
                            })
                        })
                    ]
                })
            })
        ]
    });
});
